import { Flex } from '@lokalise/louis';
import type { FormikConfig, FormikValues } from 'formik';
import { Formik, isFunction } from 'formik';
import { useRef } from 'react';

import useEffectOnce from '@app/hooks/useEffectOnce';

import ProgressBar from '../ProgressBar';
import { Button } from '../components';
import {
    BackButton,
    BackButtonWrapper,
    ChildrenWrapper,
    FormSetupWrapper,
    PageHeading,
    PageParagraph,
    SetupWrapper,
} from './styles';

type SetupPageWrapperProps<
    InitialValues extends FormikValues,
    Children extends FormikConfig<InitialValues>['children'],
    ValidationSchema,
> = {
    headingText: string;
    onSubmit: FormikConfig<InitialValues>['onSubmit'];
    initialValues: InitialValues;
    buttonText: string;
    children: Children;
    validationSchema: ValidationSchema;
    progressPercentage?: number;
    pageText?: string;
    validateOnChange?: boolean;
    validateOnBlur?: boolean;
    onBack?: () => void;
    loading?: boolean;
};

const SetupPageWrapper = <InitialValues extends FormikValues, ValidationSchema>({
    progressPercentage,
    headingText,
    pageText,
    initialValues,
    onSubmit,
    children,
    validateOnChange,
    validateOnBlur,
    buttonText,
    onBack,
    validationSchema,
    loading = false,
}: SetupPageWrapperProps<InitialValues, FormikConfig<InitialValues>['children'], ValidationSchema>) => {
    const wrapperEl = useRef<HTMLDivElement | null>(null);

    useEffectOnce(() => {
        // without setTimeout transition is not working for some views,
        // so this fixed it, probably due adding this in the end of the exec stack
        setTimeout(() => {
            if (wrapperEl?.current) {
                wrapperEl.current.style.opacity = '1';
            }
        }, 10);
    });

    return (
        <SetupWrapper ref={wrapperEl}>
            {progressPercentage && <ProgressBar progressPercentage={progressPercentage} />}
            <Flex gap={4}>
                <PageHeading>{headingText}</PageHeading>
                {pageText && <PageParagraph>{pageText}</PageParagraph>}
            </Flex>
            <div>
                <Formik
                    initialValues={initialValues}
                    onSubmit={onSubmit}
                    validateOnChange={validateOnChange}
                    validateOnBlur={validateOnBlur}
                    validationSchema={validationSchema}
                    enableReinitialize
                >
                    {(props) => (
                        <FormSetupWrapper>
                            <ChildrenWrapper>{isFunction(children) ? children(props) : children}</ChildrenWrapper>
                            <Button loading={loading} type="submit">
                                {buttonText}
                            </Button>
                        </FormSetupWrapper>
                    )}
                </Formik>
                {onBack && (
                    <BackButtonWrapper>
                        <BackButton onClick={onBack}>Back</BackButton>
                    </BackButtonWrapper>
                )}
            </div>
        </SetupWrapper>
    );
};

export default SetupPageWrapper;
